import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import { ApiError } from './lib/call.ts';
import { impersonation } from './lib/impersonation.ts';
import { meKey } from './lib/me.ts';
import { PATHS } from './lib/paths.ts';
import { router } from './router.tsx';

/**
 * 전역 오류 처리 — 로그인이 필요한 영역에서만 움직인다.
 * ★ 공개 화면인지는 **매치된 라우트**로 본다(경로 접두가 아니다). /admin/login 은 접두가 /admin 이어도 공개 화면이라,
 *   접두로 보면 로그인 실패(401)가 같은 화면으로 다시 이동시킨다.
 */
const onError = (error: Error) => {
  if (!(error instanceof ApiError)) return;
  const ids: string[] = router.state.matches.map((m) => m.routeId);
  const area = ids.includes('/admin') ? 'admin' : ids.includes('/app') ? 'app' : null;
  if (!area) return;
  if (area !== 'admin' && impersonation()) {
    // 대리 탭 — 로그인 화면으로 보내지 않는다. 캐시에 남은 me 를 지워 다시 묻게 하면 레이아웃이 종료 안내를 그린다.
    // ★ me 가 이미 실패 상태면 건드리지 않는다 — me 자신의 401 에서 또 지우면 재조회 → 401 → 지움이 끝없이 돈다(실측)
    if (error.status === 401 && queryClient.getQueryState(meKey(area))?.status === 'success')
      void queryClient.resetQueries({ queryKey: meKey(area) });
    return;
  }
  const p = PATHS[area];
  if (error.status === 401) void router.navigate({ to: p.login, replace: true });
  else if (error.code === 'password-change-required' && router.state.location.pathname !== p.password)
    void router.navigate({ to: p.password, replace: true });
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError }),
  mutationCache: new MutationCache({ onError }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // 401·403·404 는 다시 물어도 같다
      retry: (count, error) => !(error instanceof ApiError && error.status >= 400 && error.status < 500) && count < 2,
      refetchOnWindowFocus: false,
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
